import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { addPhoto, getPhoto } from './eleve.reducer';
import { Descriptions, Divider, Row, Col, Avatar, Badge, Upload, Tag, Switch, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, RouteComponentProps } from 'react-router-dom';
import { TextFormat } from 'react-jhipster';
import QRCode from 'qrcode.react';
import { UserOutlined } from '@ant-design/icons';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { APP_LOCAL_DATE_FORMAT, AUTHORITIES } from 'app/config/constants';
import { statutEnvoiFacture } from 'app/shared/model/eleve.model';
import EleveOptions from './options-eleve';
import { useCheckIsAnneeInactive } from 'app/hooks/useCheckIsAnneeActive';
import { Button } from 'reactstrap';
import { FaUnlink } from 'react-icons/fa';
import EleveDroitsControleAcces from 'app/entities/controle-acces/EleveDroitsControleAcces';

interface IPersonalInfoTab {
  props: RouteComponentProps<{ id: string; ecoleid: string }>;
  eleveId?: string;
  isActive: boolean;
  anneeScolaireId?: string;
}

const mayViewAccessRights = authorities =>
  hasAnyAuthority(authorities, [AUTHORITIES.ADMIN, AUTHORITIES.DIRECTEUR, AUTHORITIES.DIRECTEUR_ETUDE, AUTHORITIES.SURVEILLANT_GENERAL]);

const StudentAccessRights: React.FC<{ eleveId: string; anneeScolaireId?: string; authorities: string[] }> = ({
  eleveId,
  anneeScolaireId,
  authorities,
}) => {
  if (!anneeScolaireId || !mayViewAccessRights(authorities)) return null;

  return (
    <>
      <hr />
      <h5>Contrôle d’accès</h5>
      <EleveDroitsControleAcces eleveId={eleveId} anneeScolaireId={anneeScolaireId} />
    </>
  );
};

const PersonalInfoTab: React.FC<IPersonalInfoTab> = ({ isActive, props, anneeScolaireId }) => {
  const dispatch = useAppDispatch();
  const { entity: eleve, loading } = useAppSelector(state => state.eleve);
  const currentEcole = useAppSelector(state => state.ecole.entity);
  const account = useAppSelector(state => state.authentication.account);

  const isComptable = hasAnyAuthority(account.authorities, [AUTHORITIES.COMPTABLE]);
  const isDirecteur = hasAnyAuthority(account.authorities, [AUTHORITIES.DIRECTEUR]);
  const isDirecteurEtude = hasAnyAuthority(account.authorities, [AUTHORITIES.DIRECTEUR_ETUDE]);
  const isAdmin = hasAnyAuthority(account.authorities, [AUTHORITIES.ADMIN]);
  const isNotComptable = !isComptable;
  const isNotSurveillantGeneral = !hasAnyAuthority(account.authorities, [AUTHORITIES.SURVEILLANT_GENERAL]);
  const inactiveAnnee = useCheckIsAnneeInactive(anneeScolaireId);

  useEffect(() => {
    if (isActive) {
      dispatch(getPhoto(props.match.params.id));
    }
  }, [dispatch, props.match.params.id, isActive]);

  const handlePhotoUpload = {
    beforeUpload(file) {
      const formData = new FormData();
      formData.append('file', file);
      dispatch(addPhoto({ id: eleve.id, photo: formData })).then(() => {
        dispatch(getPhoto(eleve.id));
      });
    },
  };

  const handleOpenModal = () => {
    props.history.push(`/eleve/${props.match.params.ecoleid}/${anneeScolaireId}/${props.match.params.id}/details/send-facture`);
  };

  const handlePaiementUpdate = () => {
    props.history.push(`/eleve/${props.match.params.ecoleid}/${anneeScolaireId}/${props.match.params.id}/details/update-paiement`);
  };

  return (
    <Spin spinning={loading} tip="Chargement...">
      <Row className="justify-content-center">
        <Col lg={{ span: 6, offset: 0 }} xs={{ span: 20, offset: 3 }} sm={{ span: 7, offset: 0 }} md={{ span: 6, offset: 0 }}>
          <Descriptions>
            {anneeScolaireId ? (
              <Descriptions.Item>
                {(isDirecteur || isDirecteurEtude || isAdmin) && (
                  <Upload {...handlePhotoUpload} showUploadList={false} className="avatar">
                    {eleve.photo ? (
                      <Badge.Ribbon className="badge" color="cyan" text={<FontAwesomeIcon icon="pencil-alt" />}>
                        <Avatar shape="circle" src={`data:image/png;base64,${eleve.photo}`} size={150} icon={<UserOutlined />} />
                      </Badge.Ribbon>
                    ) : (
                      <Badge.Ribbon className="badge" color="cyan" text={<FontAwesomeIcon icon="pencil-alt" />}>
                        <Avatar shape="circle" size={150} icon={<UserOutlined />} />
                      </Badge.Ribbon>
                    )}
                  </Upload>
                )}
                {!isDirecteur && !isDirecteurEtude && !isAdmin && (
                  <Avatar
                    shape="circle"
                    src={eleve.photo ? `data:image/png;base64,${eleve.photo}` : ''}
                    size={150}
                    icon={<UserOutlined />}
                  />
                )}
              </Descriptions.Item>
            ) : (
              <Descriptions.Item>
                {(isDirecteur || isDirecteurEtude || isAdmin) && (
                  <Upload {...handlePhotoUpload} showUploadList={false} className="avatar">
                    {eleve.photo ? (
                      <Badge.Ribbon className="badge" color="cyan" text={<FontAwesomeIcon icon="pencil-alt" />}>
                        <Avatar shape="circle" src={`data:image/png;base64,${eleve.photo}`} size={150} icon={<UserOutlined />} />
                      </Badge.Ribbon>
                    ) : (
                      <Badge.Ribbon className="badge" color="cyan" text={<FontAwesomeIcon icon="pencil-alt" />}>
                        <Avatar shape="circle" size={150} icon={<UserOutlined />} />
                      </Badge.Ribbon>
                    )}
                  </Upload>
                )}
                {!isDirecteur && !isDirecteurEtude && !isAdmin && (
                  <Avatar
                    shape="circle"
                    src={eleve.photo ? `data:image/png;base64,${eleve.photo}` : ''}
                    size={150}
                    icon={<UserOutlined />}
                  />
                )}
              </Descriptions.Item>
            )}
          </Descriptions>

          {anneeScolaireId ? (
            <Descriptions column={1} className="ml-2">
              <Descriptions.Item label="Statut">
                {eleve.statutPresence === 'PRESENT' ? (
                  <Tag color="green">{eleve.statutPresence}</Tag>
                ) : (
                  <Tag color="red">{eleve.statutPresence}</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Etat">
                {eleve.etatPaiement === 'A_JOUR' ? (
                  <Tag color="green">{eleve.etatPaiement}</Tag>
                ) : (
                  <Tag color="red">{eleve.etatPaiement}</Tag>
                )}
                {(isComptable || isDirecteur || isDirecteurEtude || isAdmin) && (
                  <Switch disabled={inactiveAnnee} size="small" checked={eleve.etatPaiement === 'A_JOUR'} onClick={handlePaiementUpdate} />
                )}
              </Descriptions.Item>
              <Descriptions.Item>
                {eleve.support ? <QRCode value={eleve.support.qrcode} /> : <span className="qrcode"></span>}
              </Descriptions.Item>
              <Descriptions.Item>
                {isAdmin &&
                  (eleve.support ? (
                    <Button
                      color="danger"
                      size="sm"
                      style={{
                        borderRadius: '5px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                      }}
                      onClick={() =>
                        props.history.push(
                          `/eleve/${props.match.params.ecoleid}/${anneeScolaireId}/${props.match.params.id}/details/dissociate-support`
                        )
                      }
                    >
                      <FaUnlink />
                      Dissocier support
                    </Button>
                  ) : (
                    <span></span>
                  ))}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <></>
          )}
        </Col>
        <Divider type="vertical" style={{ height: 'auto' }} />
        <Col lg={{ span: 10 }} xs={{ span: 24 }} sm={{ span: 10 }} md={{ span: 10 }}>
          <h2 style={{ textAlign: 'center' }} className="p-4">
            {eleve.firstName} {eleve.lastName}
          </h2>
          <Descriptions column={1}>
            <Descriptions.Item label="Matricule">{eleve.matricule}</Descriptions.Item>
            {eleve?.birthdate && (
              <Descriptions.Item label="Date Naissance">
                <TextFormat type="date" value={eleve.birthdate} format={APP_LOCAL_DATE_FORMAT} />
              </Descriptions.Item>
            )}
            {eleve?.adresse && <Descriptions.Item label="Adresse">{eleve.adresse}</Descriptions.Item>}
          </Descriptions>
          <hr />
          <h5>Informations administratives</h5>
          <Descriptions column={1}>
            {anneeScolaireId && <Descriptions.Item label="Classe">{eleve.classe?.nom}</Descriptions.Item>}
            <Descriptions.Item label="Téléphone">{eleve.telephone}</Descriptions.Item>
            <Descriptions.Item label="Montant Scolarite">{eleve.montantScolarite} CFA</Descriptions.Item>
            {anneeScolaireId && (
              <Descriptions.Item label="Statut Envoi Facture">
                <>
                  {eleve.statutEnvoiFacture === statutEnvoiFacture.ACTIVE ? (
                    <Tag color="green">Activé</Tag>
                  ) : (
                    <Tag color="red">Désactivé</Tag>
                  )}
                  {(isComptable || isDirecteur || isDirecteurEtude || isAdmin) && (
                    <Switch
                      disabled={inactiveAnnee}
                      size="small"
                      checked={eleve.statutEnvoiFacture === statutEnvoiFacture.ACTIVE}
                      onClick={handleOpenModal}
                    />
                  )}
                </>
              </Descriptions.Item>
            )}
          </Descriptions>
          <>
            <hr />
            <h5>Informations Parents</h5>
            <Descriptions column={1}>
              <Descriptions.Item label="Parent">
                {eleve.parent?.firstName} {eleve.parent?.lastName}
                <Link to={`/parent/${currentEcole?.id}/${eleve.parent?.id}/details`}>
                  <FontAwesomeIcon icon="eye" style={{ marginLeft: 8 }} />
                </Link>
              </Descriptions.Item>
              <Descriptions.Item label="Téléphone">{eleve.parent?.telephone}</Descriptions.Item>
            </Descriptions>
          </>
          <StudentAccessRights eleveId={props.match.params.id} anneeScolaireId={anneeScolaireId} authorities={account.authorities} />
        </Col>
        {isNotComptable && isNotSurveillantGeneral && !anneeScolaireId && (
          <>
            <Divider type="vertical" style={{ height: 'auto' }} />
            <Col lg={{ span: 4 }} xs={{ span: 24 }} md={{ span: 6 }} sm={{ span: 5 }} style={{ textAlign: 'center' }}>
              <EleveOptions props={props} />
            </Col>
          </>
        )}
      </Row>
    </Spin>
  );
};

export default PersonalInfoTab;
